import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import DualActionFooter from '@/components/feature/DualActionFooter';
import HowWeVetEngineers from '@/components/feature/HowWeVetEngineers';

const generateServiceSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Dedicated Development Team",
  "provider": { "@type": "Organization", "name": "Thoughtic Solutions", "url": "https://thoughtic.com" },
  "description": "Fully managed cross-functional engineering teams assembled in 2-4 weeks. Includes Tech Lead, engineers, QA, and DevOps — we handle recruitment, management, and delivery.",
  "serviceType": "Dedicated Development Team",
  "url": "https://thoughtic.com/dedicated-development-team",
  "areaServed": { "@type": "Country", "name": "Worldwide" }
});

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

/* ── Hero ── */
function Hero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-16 md:pt-40 md:pb-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20corporate%20background%20with%20global%20team%20collaboration%20visualization%2C%20remote%20developers%20connected%20via%20network%20lines%20across%20world%20map%2C%20deep%20navy%20blue%20with%20warm%20amber%20and%20teal%20green%20accents%2C%20professional%20outsourcing%20and%20team%20building%20aesthetic%2C%20soft%20bokeh%20lighting%2C%20modern%20workforce%20management%20theme%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay&width=1600&height=900&seq=dedicated-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-team-fill text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Fully Managed Teams</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Dedicated Development
            <br />
            <span className="text-primary-400">Team for Hire</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            Build a fully managed, cross-functional engineering team that works as an extension of your company. We handle recruitment, management, and delivery — you steer the roadmap.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Build Your Team
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              How Engagement Works
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

/* ── Stats Bar ── */
function StatsBar() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const stats = [
    { value: '50+', label: 'Active Teams', icon: 'ri-team-fill' },
    { value: '4.2yr', label: 'Avg. Retention', icon: 'ri-medal-line' },
    { value: '2-4w', label: 'Team Assembly', icon: 'ri-time-line' },
    { value: '98%', label: 'Satisfaction', icon: 'ri-thumb-up-line' },
  ];

  return (
    <section className="py-10 md:py-14 bg-white border-b border-gray-100" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.08, ...springTransition }}
              className="text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-50 mx-auto mb-2">
                <i className={`${stat.icon} text-primary-600 text-lg`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-0.5">{stat.value}</div>
              <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Why Dedicated Teams ── */
function WhyDedicatedTeams() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const reasons = [
    {
      icon: 'ri-team-fill',
      title: 'Fully Managed Squads',
      desc: 'Each dedicated team includes a Tech Lead, backend and frontend engineers, QA, and DevOps — all managed by us, aligned with your roadmap. You set priorities; we handle delivery.',
    },
    {
      icon: 'ri-rocket-line',
      title: 'Aligned with Your Goals',
      desc: 'Our teams do not just write code. They understand your business metrics, user journeys, and competitive landscape. Every sprint is tied to outcomes that move your product forward.',
    },
    {
      icon: 'ri-bar-chart-grouped-line',
      title: 'Elastic Scalability',
      desc: 'Start with 3 engineers and grow to 15 as your product matures. Add specialized pods for ML, mobile, or DevOps without restarting the hiring process from scratch.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Zero Management Overhead',
      desc: 'Sprint planning, code reviews, standups, retrospectives, and performance reviews — we run it all. You get a single point of contact and weekly executive summaries.',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-team-fill text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Build a Dedicated Team Through Thoughtic</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Your Team,
            <span className="text-primary-600"> Without the Org Chart</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Skip the 6-month hiring cycle, office setup, and management layer. We assemble and operate a dedicated development team that feels like your own — minus the overhead.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {reasons.map((r, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${r.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{r.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Team Structure ── */
function TeamStructure() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const roles = [
    { name: 'Tech Lead', count: '1', desc: 'Architecture, mentoring, and stakeholder communication', icon: 'ri-user-star-line', color: 'bg-navy-900 text-white' },
    { name: 'Backend Engineers', count: '2–4', desc: 'APIs, microservices, databases, and integrations', icon: 'ri-server-line', color: 'bg-emerald-500 text-white' },
    { name: 'Frontend Engineers', count: '1–3', desc: 'UI/UX implementation, component libraries, performance', icon: 'ri-layout-masonry-line', color: 'bg-sky-500 text-white' },
    { name: 'QA Engineer', count: '1', desc: 'Automated testing, regression suites, release validation', icon: 'ri-bug-line', color: 'bg-rose-500 text-white' },
    { name: 'DevOps Engineer', count: '0–1', desc: 'CI/CD, infrastructure, monitoring, and security', icon: 'ri-cloud-line', color: 'bg-violet-500 text-white' },
    { name: 'Product Manager', count: '0–1', desc: 'Backlog grooming, sprint planning, roadmap alignment', icon: 'ri-folder-chart-line', color: 'bg-amber-500 text-white' },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-primary-100/40 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-amber-100/40 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-stack-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Typical Team Composition</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            A Complete
            <span className="text-primary-600"> Cross-Functional Squad</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Every dedicated team is assembled with the exact skill mix your product needs. No gaps, no unnecessary hires.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {roles.map((role, i) => (
            <motion.div
              key={role.name}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.08, ...springTransition }}
              className="group bg-white rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${role.color}`}>
                  <i className={`${role.icon} text-xl`}></i>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-bold text-navy-900">{role.name}</h3>
                  <span className="text-sm font-semibold text-primary-600">{role.count} per squad</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{role.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── How It Works ── */
function HowItWorks() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const steps = [
    {
      num: '01',
      title: 'Discovery & Planning',
      desc: 'We workshop your product vision, tech stack, and timeline. Then we design the optimal team structure, skill mix, and sprint cadence.',
    },
    {
      num: '02',
      title: 'Team Assembly',
      desc: 'Our recruiters tap into 1,000+ vetted engineers to find the perfect Tech Lead and squad members. Interviews happen within 5 business days.',
    },
    {
      num: '03',
      title: 'Onboarding & Ramp',
      desc: 'Engineers join your tools, attend your standups, and ship their first commits within the first week. We handle compliance, payroll, and equipment.',
    },
    {
      num: '04',
      title: 'Delivery & Scale',
      desc: 'Weekly executive reports, quarterly roadmap reviews, and continuous talent optimization. Scale the team up or down as your product evolves.',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-route-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Our Process</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            From Brief to
            <span className="text-primary-600"> Shipping Squad</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Launching a dedicated development team does not have to take months. Here is how we get you operational in under four weeks.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="text-3xl font-black text-primary-200 mb-3">{step.num}</div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Trust Signals ── */
function TrustSignals() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const signals = [
    { icon: 'ri-shield-check-line', title: 'SOC 2 Type II', desc: 'Certified security and data protection practices for all team operations.' },
    { icon: 'ri-file-shield-line', title: 'NDA & IP Protection', desc: 'All code ownership transfers to you on day one. Full IP assignment clauses.' },
    { icon: 'ri-lock-line', title: 'Isolated Environments', desc: 'Dedicated VPNs, zero-trust access, and client-managed infrastructure.' },
    { icon: 'ri-global-line', title: 'GDPR & HIPAA Ready', desc: 'Compliance frameworks built into every engagement and team workflow.' },
  ];

  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4">
            <i className="ri-shield-check-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Enterprise Trust</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4">
            Security & Compliance
            <span className="text-primary-400"> Built In</span>
          </h2>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto">
            Every dedicated team operates under the same enterprise-grade safeguards we offer to Fortune 500 clients.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {signals.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.08, ...springTransition }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 text-center"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mx-auto mb-4">
                <i className={`${s.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base font-bold text-white mb-1">{s.title}</h3>
              <p className="text-sm text-white/60">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Case Study Snippet ── */
function CaseStudySnippet() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4">
              <i className="ri-briefcase-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium text-gray-700">Client Success</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4">
              SaaS Company Builds
              <span className="text-primary-600"> 8-Person Squad</span> in 3 Weeks
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
              A B2B SaaS company needed to launch a new analytics module but had no available internal engineers. We assembled a dedicated team of 8 — Tech Lead, 4 full-stack engineers, 2 QA, and a DevOps specialist — who shipped the module from zero to beta in 10 weeks, fully managed by Thoughtic.
            </p>
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div>
                <div className="text-2xl font-bold text-primary-600">3 Weeks</div>
                <div className="text-xs text-gray-500">To full team</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-600">8 Engineers</div>
                <div className="text-xs text-gray-500">Assembled</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-600">10 Weeks</div>
                <div className="text-xs text-gray-500">To beta launch</div>
              </div>
            </div>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Read Full Case Study
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, ...springTransition, duration: 0.7 }}
            className="relative"
          >
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20SaaS%20analytics%20dashboard%20interface%20on%20large%20monitor%20screen%20in%20bright%20contemporary%20office%2C%20dedicated%20development%20team%20collaboration%20workspace%20with%20multiple%20screens%20showing%20agile%20board%20and%20code%20repository%2C%20warm%20professional%20lighting%2C%20clean%20corporate%20photography%20style%2C%20sage%20green%20and%20warm%20amber%20tones%2C%20shallow%20depth%20of%20field&width=800&height=600&seq=dedicated-case-1&orientation=landscape"
              alt="Dedicated team case study"
              className="rounded-2xl shadow-2xl shadow-gray-200 w-full"
            />
            <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-white rounded-xl p-4 shadow-xl border border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
              </div>
              <p className="text-xs text-gray-600">"It felt like we hired an entire division overnight."</p>
              <p className="text-[10px] text-gray-400 mt-0.5">— CEO, B2B SaaS Company</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ── Final CTA ── */
function FinalCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 right-0 w-[400px] h-[400px] bg-primary-100/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 left-0 w-[400px] h-[400px] bg-amber-100/30 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-6 shadow-sm">
            <i className="ri-team-fill text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Start Building This Month</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6">
            Launch Your Dedicated
            <span className="text-primary-600"> Development Team</span>
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 md:mb-10">
            From a 3-engineer pod to a 15-person product squad — we build dedicated teams that ship quality code, communicate clearly, and scale on demand.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Build Your Team
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 border-2 border-navy-900/20 text-navy-900 text-base font-semibold rounded-xl hover:bg-navy-900 hover:text-white hover:border-navy-900 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View Pricing
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function DedicatedDevelopmentTeamPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generateServiceSchema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <PageLayout
      title="Dedicated Development Team — Fully Managed Engineering Squad"
      description="Build a fully managed dedicated development team. Cross-functional squads assembled in 2-4 weeks. We handle recruitment, management, and delivery — you steer the roadmap."
      keywords="dedicated development team, managed engineering squad, hire full development team, outsourced development team, cross-functional engineering team, offshore dedicated team, software development team for hire"
    >
      <Hero />
      <StatsBar />
      <WhyDedicatedTeams />
      <TeamStructure />
      <HowItWorks />
      <TrustSignals />
      <HowWeVetEngineers
        accentColor="primary"
        heading="How We Build Every Dedicated Team"
        specialtyNote="For dedicated team engagements, we additionally assess team leadership ability, sprint planning experience, and cross-functional communication skills."
      />
      <CaseStudySnippet />
      <FinalCTA />
      <DualActionFooter />
    </PageLayout>
  );
}